import { useMemo, useState, useEffect } from "react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Spinner from "../common/Spinner";
import { updateBookingStatus, updateBookingDetails, deleteBooking, sendQuote } from "../../api/bookings";
import { fetchServices } from "../../api/services";
import { formatUSD } from "../../utils/money";
import { TaxCountyPicker } from "../common/TaxCountyPicker";
import { formatRatePercent } from "../../api/tax";
import { formatEasternDateTime, formatEasternTime } from "../../utils/dates";

export default function EnhancedBookingDetailModal({ isOpen, booking, onClose }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [quoteMode, setQuoteMode] = useState(false);
  const [quotePrice, setQuotePrice] = useState("");
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [editData, setEditData] = useState({
    location_address: "",
    location_notes: "",
    internal_notes: "",
    units: 1,
    custom_price_cents: null,
    discount_amount_cents: null,
    discount_reason: "",
    tax_county: null,
    tax_rate_basis_points: null,
  });

  // Separate state for input display values (strings)
  const [priceInputs, setPriceInputs] = useState({
    custom_price: "",
    discount_amount: ""
  });

  // Reset state when modal opens or booking changes
  useEffect(() => {
    if (isOpen) {
      setSaving(false);
      setErr("");
      setEditMode(false);
      setDeleteMode(false);
      
      if (booking) {
        setEditData({
          location_address: booking.location_address || "",
          location_notes: booking.location_notes || "",
          internal_notes: booking.internal_notes || "",
          units: booking.units || 1,
          custom_price_cents: booking.custom_price_cents || null,
          discount_amount_cents: booking.discount_amount_cents || null,
          discount_reason: booking.discount_reason || "",
          tax_county: booking.tax_county || null,
          tax_rate_basis_points:
            booking.tax_rate_basis_points != null ? booking.tax_rate_basis_points : null,
        });

        // Initialize display values (reset every time a different booking opens)
        setPriceInputs({
          custom_price: booking.custom_price_cents ? (booking.custom_price_cents / 100).toString() : "",
          discount_amount: booking.discount_amount_cents ? (booking.discount_amount_cents / 100).toString() : ""
        });
      }
      
      // Load services for pricing packages
      loadServices();
    }
  }, [isOpen, booking?.id]);

  async function loadServices() {
    setLoadingServices(true);
    try {
      const data = await fetchServices({ includeInactive: false });
      const activeServices = Array.isArray(data) ? data : [];
      activeServices.sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
      setServices(activeServices);
    } catch (error) {
      console.error("Failed to load services:", error);
    } finally {
      setLoadingServices(false);
    }
  }

  const units = booking?.units ?? 1;

  const humanRange = useMemo(() => {
    if (!booking) return "";
    const st = new Date(booking.start_at);
    const en = new Date(booking.end_at);
    return `${formatEasternDateTime(st)} — ${formatEasternTime(en)}`;
  }, [booking]);

  async function setStatus(next) {
    if (!booking?.id) return;
    setErr("");
    setSaving(true);
    try {
      await updateBookingStatus(booking.id, next);
      onClose?.({ didUpdate: true });
    } catch (e) {
      setErr(e.message || "Failed to update booking.");
    } finally {
      setSaving(false);
    }
  }

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");

    try {
      await updateBookingDetails(booking.id, editData);
      setEditMode(false);
      onClose?.({ didUpdate: true });
    } catch (error) {
      setErr(error.message || "Failed to update booking");
    } finally {
      setSaving(false);
    }
  };

  const handleSendQuote = async () => {
    const cents = Math.round(parseFloat(quotePrice) * 100);
    if (!cents || cents <= 0 || Number.isNaN(cents)) {
      setErr("Enter a quote amount greater than $0.");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      await sendQuote(booking.id, cents);
      setQuoteMode(false);
      onClose?.({ didUpdate: true });
    } catch (error) {
      setErr(error.message || "Failed to send quote");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    setErr("");

    try {
      await deleteBooking(booking.id);
      onClose?.({ didUpdate: true, deleted: true });
    } catch (error) {
      setErr(error.message || "Failed to delete booking");
    } finally {
      setSaving(false);
    }
  };

  // If in quote-sending mode
  if (quoteMode) {
    return (
      <Modal isOpen={isOpen} title="Send Quote" onClose={() => setQuoteMode(false)} maxWidth="max-w-md">
        <div className="space-y-4">
          <div className="bg-birdie-cream border border-birdie-green/20 rounded-xl p-4">
            <div className="text-sm text-birdie-charcoal">
              <div className="font-medium">{booking?.customer_name}</div>
              <div>{booking?.service_name}</div>
              <div>{humanRange}</div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-birdie-charcoal mb-1">
              Quote amount (subtotal, before tax)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-birdie-charcoal/60">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={quotePrice}
                onChange={(e) => setQuotePrice(e.target.value)}
                className="w-full pl-7 pr-3 py-2 border border-birdie-green/25 rounded-lg focus:outline-none focus:ring-2 focus:ring-birdie-green/40"
                placeholder="0.00"
                autoFocus
              />
            </div>
            <p className="text-xs text-birdie-charcoal/60 mt-2">
              Sales tax is calculated automatically. The customer gets an email with the
              full price breakdown and an accept link; accepting confirms the booking.
            </p>
          </div>

          {err && <div className="text-sm text-red-600">{err}</div>}

          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => setQuoteMode(false)}
              disabled={saving}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button onClick={handleSendQuote} disabled={saving} className="flex-1">
              {saving ? <Spinner size="sm" /> : "Send Quote"}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // If in delete confirmation mode
  if (deleteMode) {
    return (
      <Modal isOpen={isOpen} title="Confirm Delete" onClose={() => setDeleteMode(false)} maxWidth="max-w-md">
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-red-600">
            <WarningIcon className="h-8 w-8" />
            <div>
              <div className="font-medium">Delete Booking</div>
              <div className="text-sm text-birdie-charcoal/70">This action cannot be undone.</div>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="text-sm text-red-800">
              <div className="font-medium">{booking?.customer_name}</div>
              <div>{booking?.service_name}</div>
              <div>{humanRange}</div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => setDeleteMode(false)}
              disabled={saving}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={saving}
              className="flex-1"
            >
              {saving ? <Spinner size="sm" /> : "Delete Booking"}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      title={`${booking?.customer_name || 'Customer'} • ${booking?.service_name || 'Event Request'}`}
      onClose={() => onClose?.({ didUpdate: false })}
      maxWidthClass="max-w-none w-[95vw]"
      heightClass="h-[90vh]"
    >
      {!booking ? (
        <Spinner label="Loading..." />
      ) : (
        <div className="h-full flex flex-col space-y-8">
          {err && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
              <div className="flex items-center gap-3">
                <WarningIcon className="h-6 w-6 text-red-500" />
                <span className="font-semibold text-lg">Error:</span>
              </div>
              <div className="mt-2 text-base">{err}</div>
            </div>
          )}

          {/* Two-Column Layout */}
          <div className="flex-1 grid grid-cols-1 xl:grid-cols-2 gap-12">
            {/* Left Column - Booking Information */}
            <div className="space-y-8">
              {/* Customer & Event Info */}
              <div className="bg-gradient-to-r from-birdie-cream to-birdie-beige rounded-3xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-accent text-2xl font-bold text-birdie-charcoal">Event Details</h3>
                  <StatusPill status={booking.status} />
                </div>

                <div className="space-y-5">
                  <div className="flex items-center gap-4">
                    <UserIcon className="h-6 w-6 text-birdie-green" />
                    <div>
                      <div className="font-semibold text-lg text-birdie-charcoal">{booking.customer_name}</div>
                      <div className="text-base text-birdie-charcoal/70">{booking.service_name}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <CalendarIcon className="h-6 w-6 text-birdie-green" />
                    <div className="text-base font-medium text-birdie-charcoal">{humanRange}</div>
                  </div>

                  {booking.location_address && (
                    <div className="flex items-start gap-4">
                      <LocationIcon className="h-6 w-6 text-birdie-green mt-0.5" />
                      <div className="text-base text-birdie-charcoal">{booking.location_address}</div>
                    </div>
                  )}

                  {units > 1 && (
                    <div className="flex items-center gap-4">
                      <HashIcon className="h-6 w-6 text-birdie-green" />
                      <div className="text-base font-medium text-birdie-charcoal">Units: {units}</div>
                    </div>
                  )}

                  {(booking.guest_count || booking.event_type) && (
                    <div className="flex items-center gap-4">
                      <UserIcon className="h-6 w-6 text-birdie-green" />
                      <div className="text-base font-medium text-birdie-charcoal">
                        {[
                          booking.guest_count ? `${booking.guest_count} guests` : null,
                          booking.event_type || null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {(booking.location_notes || booking.internal_notes || booking.special_requests) && (
                <div className="bg-white border-2 border-birdie-green/15 rounded-3xl p-8">
                  <h4 className="font-accent text-xl font-bold text-birdie-charcoal mb-6">📝 Notes</h4>
                  <div className="space-y-6">
                    {booking.special_requests && (
                      <div>
                        <div className="text-base font-semibold text-birdie-charcoal mb-3">Special Requests</div>
                        <div className="text-base text-birdie-charcoal/70 whitespace-pre-wrap bg-birdie-beige rounded-2xl p-5 leading-relaxed">
                          {booking.special_requests}
                        </div>
                      </div>
                    )}
                    {booking.location_notes && (
                      <div>
                        <div className="text-base font-semibold text-birdie-charcoal mb-3">Location Notes</div>
                        <div className="text-base text-birdie-charcoal/70 whitespace-pre-wrap bg-birdie-beige rounded-2xl p-5 leading-relaxed">
                          {booking.location_notes}
                        </div>
                      </div>
                    )}
                    {booking.internal_notes && (
                      <div>
                        <div className="text-base font-semibold text-birdie-charcoal mb-3">Internal Notes</div>
                        <div className="text-base text-birdie-charcoal/70 whitespace-pre-wrap bg-birdie-beige rounded-2xl p-5 leading-relaxed">
                          {booking.internal_notes}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              {!editMode && (
                <div className="bg-white border-2 border-birdie-green/15 rounded-3xl p-8">
                  <h4 className="font-accent text-xl font-bold text-birdie-charcoal mb-6">⚡ Quick Actions</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <Button
                      onClick={() => {
                        setQuotePrice(
                          booking.custom_price_cents
                            ? (booking.custom_price_cents / 100).toFixed(2)
                            : ""
                        );
                        setQuoteMode(true);
                      }}
                      disabled={saving || booking.status === "canceled"}
                      className="justify-center py-4 text-base font-semibold bg-birdie-mustard hover:bg-birdie-mustard-dark text-birdie-charcoal"
                    >
                      💰 {booking.status === "quoted" ? "Re-send Quote" : "Send Quote"}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setStatus("pending")}
                      disabled={saving}
                      className="justify-center py-4 text-base font-semibold"
                    >
                      ⏳ Pending
                    </Button>
                    <Button
                      onClick={() => setStatus("confirmed")}
                      disabled={saving}
                      className="justify-center py-4 text-base font-semibold bg-birdie-green hover:bg-birdie-green-dark"
                    >
                      ✅ Confirm
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => setStatus("canceled")}
                      disabled={saving}
                      className="justify-center py-4 text-base font-semibold"
                    >
                      ❌ Cancel
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setDeleteMode(true)}
                      disabled={saving}
                      className="justify-center py-4 text-base font-semibold text-red-600 hover:text-red-700"
                    >
                      🗑️ Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Pricing & Packages */}
            <div className="space-y-8">
              {/* Current Pricing */}
              <div className="bg-gradient-to-r from-birdie-green/10 to-birdie-green/5 rounded-3xl p-8">
                <h3 className="font-accent text-2xl font-bold text-birdie-charcoal mb-6">💰 Pricing</h3>

                {booking.custom_price_cents || booking.discount_amount_cents ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-3 px-5 bg-white rounded-xl shadow-sm">
                      <span className="text-sm text-birdie-charcoal/70">Subtotal</span>
                      <span className="text-base font-semibold text-birdie-charcoal">
                        {formatUSD(booking.custom_price_cents || 0)}
                      </span>
                    </div>
                    {booking.discount_amount_cents > 0 && (
                      <div className="flex justify-between items-center py-3 px-5 bg-red-50 rounded-xl border border-red-200">
                        <span className="text-sm font-semibold text-red-700">
                          Discount {booking.discount_reason && `(${booking.discount_reason})`}
                        </span>
                        <span className="text-base font-bold text-red-600">
                          -{formatUSD(booking.discount_amount_cents)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-3 px-5 bg-white rounded-xl shadow-sm">
                      <span className="text-sm text-birdie-charcoal/70">
                        Sales tax
                        {booking.tax_county && booking.tax_rate_basis_points != null && (
                          <> ({booking.tax_county}, {formatRatePercent(booking.tax_rate_basis_points)})</>
                        )}
                      </span>
                      <span className="text-base font-semibold text-birdie-charcoal">
                        {formatUSD(booking.tax_cents || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-4 px-5 bg-birdie-green/10 rounded-2xl border border-birdie-green/30">
                      <span className="text-base font-bold text-birdie-green">Total billed</span>
                      <span className="text-2xl font-bold text-birdie-green">
                        {formatUSD(
                          (booking.custom_price_cents || 0)
                          - (booking.discount_amount_cents || 0)
                          + (booking.tax_cents || 0)
                        )}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-xl text-birdie-charcoal/60 mb-3">💭 No pricing set yet</div>
                    <div className="text-base text-birdie-warmgray">Click "Set Pricing" below to add custom pricing</div>
                  </div>
                )}
              </div>

              {/* Service Packages */}
              {!editMode && (
                <div className="bg-white border-2 border-birdie-green/15 rounded-3xl p-8">
                  <h4 className="font-accent text-xl font-bold text-birdie-charcoal mb-6">📦 Service Packages</h4>
                  {loadingServices ? (
                    <div className="text-center py-8">
                      <Spinner size="sm" />
                      <div className="text-base text-birdie-charcoal/60 mt-3">Loading packages...</div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {services.slice(0, 4).map(service => (
                        <div
                          key={service.id}
                          className="flex items-center justify-between p-5 border-2 border-birdie-green/15 rounded-2xl hover:bg-birdie-cream hover:border-birdie-green cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md"
                          onClick={() => {
                            setEditData({
                              ...editData,
                              custom_price_cents: service.price_cents
                            });
                            setEditMode(true);
                          }}
                        >
                          <div className="flex-1">
                            <div className="font-semibold text-base text-birdie-charcoal">{service.name}</div>
                            {service.description && (
                              <div className="text-sm text-birdie-charcoal/70 mt-1">{service.description}</div>
                            )}
                          </div>
                          <div className="text-lg font-bold text-birdie-green ml-4">
                            {formatUSD(service.price_cents)}
                          </div>
                        </div>
                      ))}
                      {services.length > 4 && (
                        <div className="text-sm text-birdie-charcoal/60 text-center pt-3">
                          And {services.length - 4} more packages available...
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Custom Pricing Form */}
              {editMode ? (
                <form onSubmit={handleEdit} className="bg-white border-2 border-birdie-green/15 rounded-3xl p-8">
                  <h4 className="font-accent text-2xl font-bold text-birdie-charcoal mb-8">✏️ Edit Pricing & Details</h4>

                  <div className="space-y-8">
                    {/* Location Fields */}
                    <div>
                      <label className="block text-base font-semibold text-birdie-charcoal mb-3">
                        Location Address
                      </label>
                      <input
                        type="text"
                        value={editData.location_address}
                        onChange={(e) => setEditData({ ...editData, location_address: e.target.value })}
                        className="block w-full px-4 py-4 border-2 border-birdie-green/15 rounded-2xl text-base focus:ring-2 focus:ring-birdie-mustard/40 focus:border-birdie-green"
                      />
                    </div>

                    <TaxCountyPicker
                      address={editData.location_address}
                      county={editData.tax_county}
                      rateBasisPoints={editData.tax_rate_basis_points}
                      autoDetected={false}
                      onChange={({ county, rateBasisPoints }) =>
                        setEditData({
                          ...editData,
                          tax_county: county,
                          tax_rate_basis_points: rateBasisPoints,
                        })
                      }
                    />

                    <div>
                      <label className="block text-base font-semibold text-birdie-charcoal mb-3">
                        Override tax rate (%)
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        min="0"
                        max="15"
                        value={
                          editData.tax_rate_basis_points != null
                            ? (editData.tax_rate_basis_points / 100).toString()
                            : ""
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          setEditData({
                            ...editData,
                            tax_rate_basis_points:
                              v === "" ? null : Math.round(parseFloat(v) * 100),
                          });
                        }}
                        className="block w-full px-4 py-3 border-2 border-birdie-green/15 rounded-2xl text-base focus:ring-2 focus:ring-birdie-mustard/40 focus:border-birdie-green"
                        placeholder="Leave blank to use county default"
                      />
                      <p className="text-xs text-birdie-charcoal/60 mt-1">
                        County default: {formatRatePercent(editData.tax_rate_basis_points)} — enter a custom rate only if the county setting is wrong.
                      </p>
                    </div>

                    <div>
                      <label className="block text-base font-semibold text-birdie-charcoal mb-3">
                        Internal Notes
                      </label>
                      <textarea
                        rows={4}
                        value={editData.internal_notes}
                        onChange={(e) => setEditData({ ...editData, internal_notes: e.target.value })}
                        className="block w-full px-4 py-4 border-2 border-birdie-green/15 rounded-2xl text-base focus:ring-2 focus:ring-birdie-mustard/40 focus:border-birdie-green"
                      />
                    </div>

                    {/* Pricing Section */}
                    <div className="border-t-2 border-birdie-green/15 pt-8">
                      <h5 className="font-accent text-xl font-bold text-birdie-charcoal mb-6">💰 Pricing</h5>

                      <div className="space-y-6">
                        <div>
                          <label className="block text-base font-semibold text-birdie-charcoal mb-3">
                            Custom Price (Total Event Cost)
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-birdie-charcoal/60 text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={priceInputs.custom_price}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPriceInputs({ ...priceInputs, custom_price: value });
                                // Update editData with cents value
                                setEditData({
                                  ...editData,
                                  custom_price_cents: value ? Math.round(parseFloat(value || 0) * 100) : null
                                });
                              }}
                              placeholder="Enter total price (e.g., 500)"
                              className="block w-full pl-8 pr-3 py-3 border border-birdie-green/15 rounded-xl focus:ring-2 focus:ring-birdie-mustard/40 focus:border-birdie-green"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-birdie-charcoal mb-2">
                            Discount Amount
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-birdie-charcoal/60 text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={priceInputs.discount_amount}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPriceInputs({ ...priceInputs, discount_amount: value });
                                // Update editData with cents value
                                setEditData({
                                  ...editData,
                                  discount_amount_cents: value ? Math.round(parseFloat(value || 0) * 100) : null
                                });
                              }}
                              placeholder="Enter discount (e.g., 50)"
                              className="block w-full pl-8 pr-3 py-3 border border-birdie-green/15 rounded-xl focus:ring-2 focus:ring-birdie-mustard/40 focus:border-birdie-green"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-birdie-charcoal mb-2">
                            Discount Reason
                          </label>
                          <select
                            value={editData.discount_reason}
                            onChange={(e) => setEditData({ ...editData, discount_reason: e.target.value })}
                            className="block w-full px-3 py-3 border border-birdie-green/15 rounded-xl focus:ring-2 focus:ring-birdie-mustard/40 focus:border-birdie-green"
                          >
                            <option value="">No discount reason</option>
                            <option value="Repeat customer">Repeat customer</option>
                            <option value="Group discount">Group discount</option>
                            <option value="Corporate rate">Corporate rate</option>
                            <option value="Promotional offer">Promotional offer</option>
                            <option value="Referral discount">Referral discount</option>
                            <option value="Last minute booking">Last minute booking</option>
                            <option value="Custom arrangement">Custom arrangement</option>
                          </select>
                        </div>

                        {/* Quick Pricing Buttons */}
                        <div className="border-t border-birdie-green/15 pt-4">
                          <div className="text-sm font-medium text-birdie-charcoal mb-3">Quick Price Options</div>
                          <div className="grid grid-cols-2 gap-2">
                            {[500, 750, 1000, 1500].map(amount => (
                              <button
                                key={amount}
                                type="button"
                                onClick={() => {
                                  setEditData({
                                    ...editData,
                                    custom_price_cents: amount * 100
                                  });
                                  setPriceInputs({
                                    ...priceInputs,
                                    custom_price: amount.toString()
                                  });
                                }}
                                className="px-3 py-2 text-sm border border-birdie-green/15 rounded-lg hover:bg-birdie-beige transition-colors"
                              >
                                ${amount}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-4 pt-8 border-t-2 border-birdie-green/15">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setEditMode(false)}
                        disabled={saving}
                        className="flex-1 py-4 text-base font-semibold"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={saving}
                        className="flex-1 py-4 text-base font-semibold bg-birdie-green hover:bg-birdie-green-dark"
                      >
                        {saving ? <Spinner size="sm" /> : "💾 Save Changes"}
                      </Button>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="text-center">
                  <Button
                    onClick={() => setEditMode(true)}
                    disabled={saving}
                    className="w-full py-4 text-lg font-semibold bg-birdie-green hover:bg-birdie-green-dark rounded-2xl"
                  >
                    💰 Set Pricing & Details
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Actions */}
          {!editMode && (
            <div className="flex items-center justify-between pt-8 border-t-2 border-birdie-green/15">
              <Button
                variant="secondary"
                onClick={() => onClose?.({ didUpdate: false })}
                disabled={saving}
                className="py-3 px-6 text-base font-medium"
              >
                Close
              </Button>
              <div className="text-base text-birdie-charcoal/60">
                💡 Click service packages to apply pricing quickly
              </div>
            </div>
          )}

          {saving && (
            <div className="flex items-center justify-center gap-2 text-sm text-birdie-charcoal/70">
              <Spinner size="sm" />
              Saving changes...
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function StatusPill({ status }) {
  const cls =
    status === "confirmed"
      ? "bg-birdie-green/10 text-birdie-green"
      : status === "quoted"
      ? "bg-birdie-green/15 text-birdie-green-dark"
      : status === "canceled"
      ? "bg-birdie-warmgray/20 text-birdie-warmgray"
      : status === "completed"
      ? "bg-birdie-charcoal/10 text-birdie-charcoal"
      : "bg-birdie-mustard/20 text-birdie-mustard-dark";

  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

// Icons
function WarningIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  );
}

function UserIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function CalendarIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function LocationIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function HashIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
    </svg>
  );
}
